import { FunctionComponent, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { NavbarUpdated } from '../../shared';
import CopyRightMessage from '../../shared/CopyRightMessage';
import { Container } from '../styled';

import {
    ResponseCard,
    StatusIcon,
    StatusTitle,
    StatusMessage,
    ActionButton,
    ButtonContentBox,
    ButtonTextBox,
    ButtonIconBox,
    ErrorDetails,
    SuccessDetails
} from './styled';

type Props = Record<string, never>;

const ServiceResponse: FunctionComponent<Props> = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [isHovered, setIsHovered] = useState(false);

    const projectId = searchParams.get('projectId');
    const status = searchParams.get('status');
    const error = searchParams.get('error');

    const isSuccess = status === 'success';
    const hasProject = !!projectId;

    // Error code to message mapping
    const getErrorMessage = (errorCode: string | null): string => {
        switch (errorCode) {
            case 'missing_code_or_state':
                return 'Authorization failed: Missing required parameters';
            case 'invalid_state':
                return 'Authorization failed: Invalid session state';
            case 'backend_api_failed':
                return 'Failed to complete Integration with backend service';
            case 'oauth_exception':
                return 'OAuth process encountered an error';
            default:
                return 'An unexpected error occurred during integration';
        }
    };

    // Get success message based on project presence
    const getSuccessMessage = (): string => {
        if (hasProject) {
            return `Integration completed successfully for project ${projectId}!`;
        }
        return 'Integration completed successfully!';
    };

    // Get navigation action based on status and project presence
    const getNextAction = () => {
        if (isSuccess) {
            if (hasProject) {
                return {
                    text: 'Continue to Project Settings',
                    action: () => navigate(`/project/${projectId}/settings`)
                };
            } else {
                return {
                    text: 'View Projects',
                    action: () => navigate('/projects')
                };
            }
        } else {
            if (hasProject) {
                return {
                    text: 'Retry GitHub Setup',
                    action: () => navigate(`/project/${projectId}/github-setup`)
                };
            } else {
                return {
                    text: 'Back to Projects',
                    action: () => navigate('/projects')
                };
            }
        }
    };

    // Get appropriate icon
    const getIcon = () => {
        if (isSuccess) {
            return '✅';
        }
        return '⚠️';
    };

    // Handle edge cases
    if (!status) {
        return (
            <NavbarUpdated>
                <Container>
                    <ResponseCard $isSuccess={false}>
                        <StatusIcon $isSuccess={false}>⚠️</StatusIcon>
                        <StatusTitle $isSuccess={false}>
                            Invalid Request
                        </StatusTitle>
                        <StatusMessage>
                            Missing required parameters. Please try again.
                        </StatusMessage>
                        <ActionButton
                            variant="contained"
                            fullWidth
                            onClick={() => navigate('/projects')}
                            onMouseEnter={() => setIsHovered(true)}
                            onMouseLeave={() => setIsHovered(false)}
                        >
                            <ButtonContentBox>
                                <ButtonTextBox $isHovered={isHovered}>
                                    Back to Projects
                                </ButtonTextBox>
                                <ButtonIconBox $isHovered={isHovered}>
                                    🐝
                                </ButtonIconBox>
                            </ButtonContentBox>
                        </ActionButton>
                    </ResponseCard>
                </Container>
                <CopyRightMessage />
            </NavbarUpdated>
        );
    }

    const nextAction = getNextAction();

    return (
        <NavbarUpdated>
            <Container>
                <ResponseCard $isSuccess={isSuccess}>
                    <StatusIcon $isSuccess={isSuccess}>{getIcon()}</StatusIcon>

                    <StatusTitle $isSuccess={isSuccess}>
                        {isSuccess
                            ? 'Integration Successful'
                            : 'Integration Failed'}
                    </StatusTitle>

                    {isSuccess ? (
                        <SuccessDetails>
                            <StatusMessage>{getSuccessMessage()}</StatusMessage>
                        </SuccessDetails>
                    ) : (
                        <ErrorDetails>
                            <StatusMessage>
                                {getErrorMessage(error)}
                            </StatusMessage>
                        </ErrorDetails>
                    )}

                    <ActionButton
                        variant="contained"
                        fullWidth
                        onClick={nextAction.action}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                    >
                        <ButtonContentBox>
                            <ButtonTextBox $isHovered={isHovered}>
                                {nextAction.text}
                            </ButtonTextBox>
                            <ButtonIconBox $isHovered={isHovered}>
                                🐝
                            </ButtonIconBox>
                        </ButtonContentBox>
                    </ActionButton>
                </ResponseCard>
            </Container>
            <CopyRightMessage />
        </NavbarUpdated>
    );
};

export default ServiceResponse;
